// Renderer for ground shapes (rectangles, polygons, ellipses)

import * as THREE from 'three'
import type { GardenScene } from './GardenScene'
import { texturePreloader } from './TexturePreloader'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'
import type { GroundShape, ProductFile } from '@/types'

export class GroundRenderer {
  private scene: GardenScene
  private meshes: Map<string, THREE.Mesh> = new Map()
  private textureLoader: THREE.TextureLoader

  constructor(scene: GardenScene) {
    this.scene = scene
    this.textureLoader = new THREE.TextureLoader()
  }

  /**
   * Render all ground shapes
   */
  async renderShapes(
    shapes: GroundShape[],
    textureUrls: Map<string, ProductFile[]> = new Map()
  ): Promise<void> {
    // Validate scene is available
    if (!this.scene || !this.scene.groundGroup) {
      throw new Error('GroundRenderer: Scene or groundGroup is not available')
    }

    // Clear existing shapes
    this.clear()

    // Sort by zIndex for proper layering
    const sortedShapes = [...shapes]
      .filter(s => !s.markedForDestruction)
      .sort((a, b) => a.zIndex - b.zIndex)

    for (let i = 0; i < sortedShapes.length; i++) {
      const shape = sortedShapes[i]
      // Small vertical offset for each layer to prevent z-fighting
      const layerOffset = 0.001 * (i + 1)
      await this.renderShape(shape, layerOffset, textureUrls)
    }
  }

  /**
   * Render a single ground shape
   */
  private async renderShape(
    shape: GroundShape,
    layerOffset: number,
    textureUrls: Map<string, ProductFile[]>
  ): Promise<void> {
    // Get texture from the map if available
    let textureFile: ProductFile | undefined
    if (shape.productId) {
      const files = textureUrls.get(shape.productId)
      textureFile = files?.find(f => f.kind === 'top')
      if (textureFile) {
        debug.log('Ground', `Found texture for product ${shape.productId}: ${textureFile.fileUrl} (${textureFile.width}x${textureFile.height})`)
      }
    }

    // Check if this should be a 3D prism (elevation > 0)
    const isPrism = shape.elevation > 0

    if (isPrism) {
      await this.renderPrism(shape, textureUrls, textureFile)
    } else {
      await this.renderFlatShape(shape, layerOffset, textureFile)
    }
  }

  /**
   * Render a flat ground shape (elevation = 0)
   */
  private async renderFlatShape(
    shape: GroundShape,
    layerOffset: number,
    textureFile?: ProductFile
  ): Promise<void> {
    let geometry: THREE.BufferGeometry

    switch (shape.type) {
      case 'rectangle':
        geometry = this.createRectangleGeometry(shape)
        break
      case 'polygon':
      case 'path':
        geometry = this.createPolygonGeometry(shape)
        break
      case 'ellipse':
        geometry = this.createEllipseGeometry(shape)
        break
      default:
        debug.warn('Ground', `Unknown shape type: ${shape.type}`)
        return
    }

    // Create material with texture if available
    const material = await this.createMaterial(shape, textureFile)

    const mesh = new THREE.Mesh(geometry, material)

    // Position the shape (x,y = center, same convention as ZoneRenderer)
    mesh.position.set(
      shape.x * DPM_SCALE,
      layerOffset,
      shape.y * DPM_SCALE
    )

    // Rotate to be horizontal (XZ plane) then apply shape rotation
    mesh.rotation.x = -Math.PI / 2
    mesh.rotation.z = -shape.rotation * Math.PI / 180

    // Enable shadow receiving (ground doesn't cast shadows)
    mesh.receiveShadow = true

    // Store reference
    mesh.name = `groundShape_${shape.id}`
    mesh.userData.shapeId = shape.id
    mesh.userData.shapeData = shape

    this.scene.groundGroup.add(mesh)
    this.meshes.set(shape.id, mesh)
  }

  /**
   * Render a 3D prism shape (elevation > 0)
   * Uses ExtrudeGeometry with same texture on all faces
   */
  private async renderPrism(
    shape: GroundShape,
    _textureUrls: Map<string, ProductFile[]>,
    textureFile?: ProductFile
  ): Promise<void> {
    const height = shape.elevation * DPM_SCALE

    // Create 2D shape for extrusion
    const shape2D = this.createShape2D(shape)
    if (!shape2D) {
      debug.warn('Ground', `Could not create 2D shape for prism: ${shape.type}`)
      return
    }

    // Create extruded geometry
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: height,
      bevelEnabled: false
    }
    const geometry = new THREE.ExtrudeGeometry(shape2D, extrudeSettings)

    // Fix UVs for proper texture mapping on all faces
    this.fixExtrudeUVs(geometry, shape, height)

    // Create single material for all faces
    const material = await this.createMaterial(shape, textureFile)

    const mesh = new THREE.Mesh(geometry, material)

    // Position the shape (x,y = center, same convention as ZoneRenderer)
    mesh.position.set(
      shape.x * DPM_SCALE,
      0,
      shape.y * DPM_SCALE
    )

    // Rotate so extrusion goes up (Y axis)
    mesh.rotation.x = -Math.PI / 2
    mesh.rotation.z = -shape.rotation * Math.PI / 180

    // Enable shadows
    mesh.castShadow = true
    mesh.receiveShadow = true

    // Store reference
    mesh.name = `groundShape_${shape.id}`
    mesh.userData.shapeId = shape.id
    mesh.userData.shapeData = shape

    this.scene.groundGroup.add(mesh)
    this.meshes.set(shape.id, mesh)

    debug.log('Ground', `Created prism: ${shape.type} with height ${shape.elevation} (${height.toFixed(2)}m)`)
  }

  /**
   * Fix UV coordinates for ExtrudeGeometry to tile texture properly
   */
  private fixExtrudeUVs(
    geometry: THREE.ExtrudeGeometry,
    shape: GroundShape,
    _height: number
  ): void {
    const uvAttr = geometry.getAttribute('uv')
    const posAttr = geometry.getAttribute('position')

    const widthWorld = shape.width * DPM_SCALE
    const heightWorld = shape.height * DPM_SCALE

    for (let i = 0; i < uvAttr.count; i++) {
      const x = posAttr.getX(i)
      const y = posAttr.getY(i)

      // Map position to UV based on shape dimensions
      // Top/bottom faces: use x,y
      // Side faces: use position along perimeter
      const u = (x + widthWorld / 2) / widthWorld
      const v = (y + heightWorld / 2) / heightWorld

      uvAttr.setXY(i, u, v)
    }

    uvAttr.needsUpdate = true
  }

  /**
   * Create a THREE.Shape from a GroundShape for extrusion
   */
  private createShape2D(shape: GroundShape): THREE.Shape | null {
    switch (shape.type) {
      case 'rectangle': {
        const width = shape.width * DPM_SCALE
        const height = shape.height * DPM_SCALE
        const rectShape = new THREE.Shape()
        rectShape.moveTo(-width / 2, -height / 2)
        rectShape.lineTo(width / 2, -height / 2)
        rectShape.lineTo(width / 2, height / 2)
        rectShape.lineTo(-width / 2, height / 2)
        rectShape.closePath()
        return rectShape
      }

      case 'polygon':
      case 'path': {
        if (!shape.points || shape.points.length < 3) {
          return null
        }
        // Points are center-relative (same as ZoneRenderer)
        const polyShape = new THREE.Shape()
        const firstPoint = shape.points[0]
        polyShape.moveTo(firstPoint.x * DPM_SCALE, -firstPoint.y * DPM_SCALE)
        for (let i = 1; i < shape.points.length; i++) {
          const point = shape.points[i]
          polyShape.lineTo(point.x * DPM_SCALE, -point.y * DPM_SCALE)
        }
        polyShape.closePath()
        return polyShape
      }

      case 'ellipse': {
        const radiusX = (shape.width / 2) * DPM_SCALE
        const radiusY = (shape.height / 2) * DPM_SCALE
        const curve = new THREE.EllipseCurve(
          0, 0,
          radiusX, radiusY,
          0, Math.PI * 2,
          false,
          0
        )
        const points = curve.getPoints(50)
        return new THREE.Shape(points)
      }

      default:
        return null
    }
  }

  /**
   * Create geometry for rectangle shape
   */
  private createRectangleGeometry(shape: GroundShape): THREE.BufferGeometry {
    const width = shape.width * DPM_SCALE
    const height = shape.height * DPM_SCALE
    return new THREE.PlaneGeometry(width, height)
  }

  /**
   * Create geometry for polygon shape
   * Points are CENTER-RELATIVE (same convention as ZoneRenderer.renderPolygonZone)
   */
  private createPolygonGeometry(shape: GroundShape): THREE.BufferGeometry {
    if (!shape.points || shape.points.length < 3) {
      // Fallback to rectangle if no points
      return this.createRectangleGeometry(shape)
    }

    // Build 2D shape from center-relative points
    // Negate Y: editor Y goes down, Three.js shape Y goes up (same as ZoneRenderer)
    const shapeObj = new THREE.Shape()
    const firstPoint = shape.points[0]
    shapeObj.moveTo(firstPoint.x * DPM_SCALE, -firstPoint.y * DPM_SCALE)

    for (let i = 1; i < shape.points.length; i++) {
      const point = shape.points[i]
      shapeObj.lineTo(point.x * DPM_SCALE, -point.y * DPM_SCALE)
    }
    shapeObj.closePath()

    // Create geometry from shape
    const geometry = new THREE.ShapeGeometry(shapeObj)

    // Fix UVs to be based on shape bounding box (0-1 normalized)
    const hw = (shape.width / 2) * DPM_SCALE
    const hh = (shape.height / 2) * DPM_SCALE
    const uvAttr = geometry.getAttribute('uv')
    const posAttr = geometry.getAttribute('position')

    for (let i = 0; i < uvAttr.count; i++) {
      const px = posAttr.getX(i)
      const py = posAttr.getY(i)

      // Map from (-hw..+hw, -hh..+hh) to (0..1, 0..1)
      const u = hw > 0 ? (px + hw) / (2 * hw) : 0
      const v = hh > 0 ? (py + hh) / (2 * hh) : 0

      uvAttr.setXY(i, u, v)
    }
    uvAttr.needsUpdate = true

    return geometry
  }

  /**
   * Create geometry for ellipse shape
   */
  private createEllipseGeometry(shape: GroundShape): THREE.BufferGeometry {
    const radiusX = (shape.width / 2) * DPM_SCALE
    const radiusY = (shape.height / 2) * DPM_SCALE

    // Create ellipse curve centered at origin
    const curve = new THREE.EllipseCurve(
      0, 0,           // Center
      radiusX, radiusY,
      0, Math.PI * 2, // Full ellipse
      false,          // Clockwise
      0               // Rotation
    )

    const points = curve.getPoints(50)
    const shapeObj = new THREE.Shape(points)

    const geometry = new THREE.ShapeGeometry(shapeObj)

    // Fix UVs to be based on ellipse bounding box (0-1 normalized)
    const uvAttr = geometry.getAttribute('uv')
    const posAttr = geometry.getAttribute('position')

    for (let i = 0; i < uvAttr.count; i++) {
      const px = posAttr.getX(i)
      const py = posAttr.getY(i)

      // Map from (-radiusX..+radiusX, -radiusY..+radiusY) to (0..1, 0..1)
      const u = (px + radiusX) / (2 * radiusX)
      const v = (py + radiusY) / (2 * radiusY)

      uvAttr.setXY(i, u, v)
    }
    uvAttr.needsUpdate = true

    return geometry
  }

  /**
   * Create material for shape (with optional texture)
   */
  private async createMaterial(
    shape: GroundShape,
    textureFile?: ProductFile
  ): Promise<THREE.MeshStandardMaterial> {
    const material = new THREE.MeshStandardMaterial({
      color: 0x7cba4f, // Default grass green
      roughness: 0.9,
      metalness: 0.0,
      side: THREE.DoubleSide
    })

    if (textureFile?.fileUrl) {
      try {
        debug.log('Ground', `Loading texture: ${textureFile.fileUrl}`)
        const texture = await this.loadTexture(textureFile.fileUrl)

        // Calculate texture repeat based on shape size and texture size
        // Both are in dots (50 dots per meter)
        // repeatX = shape.width / texture.width means: how many times texture fits in shape
        const textureWidth = textureFile.width || 50  // Default 1m if not specified
        const textureHeight = textureFile.height || 50

        const repeatX = shape.width / textureWidth
        const repeatY = shape.height / textureHeight

        debug.log('Ground', `Texture repeat: ${repeatX.toFixed(2)} x ${repeatY.toFixed(2)} (shape: ${shape.width}x${shape.height}, texture: ${textureWidth}x${textureHeight})`)

        const cloned = texture.clone()
        cloned.repeat.set(repeatX, repeatY)
        cloned.wrapS = THREE.RepeatWrapping
        cloned.wrapT = THREE.RepeatWrapping

        material.map = cloned
        material.color.setHex(0xffffff) // Reset color when using texture
        material.needsUpdate = true
        debug.success('Ground', `Texture applied to shape ${shape.id}`)
      } catch (error) {
        debug.warn('Ground', `Failed to load texture: ${textureFile.fileUrl}`, error)
      }
    }

    return material
  }

  /**
   * Load texture with caching
   * Checks preloaded textures first, then loads
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    // Check preloaded textures first (from TexturePreloader)
    const preloaded = texturePreloader.get(url)
    if (preloaded) {
      return Promise.resolve(preloaded)
    }

    // Load texture (fallback if not preloaded)
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          resolve(texture)
        },
        undefined,
        reject
      )
    })
  }

  /**
   * Render a single shape incrementally (no clear).
   * Used for adding new shapes without flickering existing ones.
   */
  async addShape(
    shape: GroundShape,
    textureUrls: Map<string, ProductFile[]> = new Map()
  ): Promise<void> {
    if (!this.scene || !this.scene.groundGroup) return
    const layerOffset = 0.001 * (this.meshes.size + 1)
    await this.renderShape(shape, layerOffset, textureUrls)
  }

  /**
   * Update an existing shape mesh in-place: position, rotation, geometry, texture repeat.
   * Reuses the existing material (avoids async texture reload → no flicker).
   * For elevated shapes (prisms), rebuilds ExtrudeGeometry in-place.
   * Falls back to full add if the mesh doesn't exist yet.
   */
  updateShape(
    shape: GroundShape,
    textureUrls: Map<string, ProductFile[]> = new Map()
  ): void {
    const existing = this.meshes.get(shape.id)
    if (!existing) {
      // Mesh not found — do a full add
      this.addShape(shape, textureUrls)
      return
    }

    const isPrism = shape.elevation > 0

    // Update position
    existing.position.x = shape.x * DPM_SCALE
    existing.position.z = shape.y * DPM_SCALE
    // Prisms sit at Y=0; flat shapes use layerOffset (preserved from initial render)
    if (isPrism) {
      existing.position.y = 0
    }

    // Update rotation
    existing.rotation.x = -Math.PI / 2
    existing.rotation.z = -shape.rotation * Math.PI / 180

    // Rebuild geometry
    const oldGeom = existing.geometry
    let newGeom: THREE.BufferGeometry

    if (isPrism) {
      // Rebuild as ExtrudeGeometry for elevated shapes
      const shape2D = this.createShape2D(shape)
      if (!shape2D) return
      const height = shape.elevation * DPM_SCALE
      newGeom = new THREE.ExtrudeGeometry(shape2D, { depth: height, bevelEnabled: false })
      this.fixExtrudeUVs(newGeom as THREE.ExtrudeGeometry, shape, height)
      // Prisms cast shadows
      existing.castShadow = true
    } else {
      // Flat geometry
      switch (shape.type) {
        case 'rectangle':
          newGeom = this.createRectangleGeometry(shape)
          break
        case 'ellipse':
          newGeom = this.createEllipseGeometry(shape)
          break
        case 'polygon':
        case 'path':
          newGeom = this.createPolygonGeometry(shape)
          break
        default:
          return
      }
      existing.castShadow = false
    }

    existing.geometry = newGeom
    oldGeom.dispose()

    // Update texture repeat on existing material (no reload)
    const material = existing.material as THREE.MeshStandardMaterial
    if (material.map) {
      let textureFile: ProductFile | undefined
      if (shape.productId) {
        const files = textureUrls.get(shape.productId)
        textureFile = files?.find(f => f.kind === 'top')
      }
      if (textureFile) {
        const textureWidth = textureFile.width || 50
        const textureHeight = textureFile.height || 50
        material.map.repeat.set(shape.width / textureWidth, shape.height / textureHeight)
      }
    }

    // Update userData
    existing.userData.shapeData = shape
  }

  /**
   * Re-sort all flat ground shape meshes by zIndex.
   * Updates Y offsets so visual stacking matches logical z-order.
   */
  resortLayers(): void {
    const flatMeshes: THREE.Mesh[] = []
    for (const mesh of this.meshes.values()) {
      const shape = mesh.userData.shapeData as GroundShape | undefined
      if (shape && shape.elevation <= 0) {
        flatMeshes.push(mesh)
      }
    }

    // Sort by zIndex (ascending = bottom to top)
    flatMeshes.sort((a, b) => {
      const zA = (a.userData.shapeData as GroundShape).zIndex
      const zB = (b.userData.shapeData as GroundShape).zIndex
      return zA - zB
    })

    // Reassign Y offsets
    for (let i = 0; i < flatMeshes.length; i++) {
      flatMeshes[i].position.y = 0.001 * (i + 1)
    }
  }

  /**
   * Remove a shape mesh
   */
  removeShape(shapeId: string): void {
    const mesh = this.meshes.get(shapeId)
    if (mesh) {
      this.scene.groundGroup.remove(mesh)
      mesh.geometry.dispose()
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose()
      }
      this.meshes.delete(shapeId)
    }
  }

  /**
   * Clear all shape meshes
   */
  clear(): void {
    for (const mesh of this.meshes.values()) {
      mesh.geometry.dispose()
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose()
      }
    }
    // Defensive check - scene and groundGroup should exist
    if (this.scene && this.scene.groundGroup) {
      this.scene.clearGroup(this.scene.groundGroup)
    }
    this.meshes.clear()
  }

  /**
   * Get shape count
   */
  get count(): number {
    return this.meshes.size
  }
}
